import { useEffect, useState } from "react";
import stepsStyles from "../../components/Stepper/Steps/steps.module.css";
import styles from "./Modal.module.css";
import { FieldError, CedulaStatus, ForeignToggle } from "../FieldHelpers";
import { useCedulaLookup } from "../../services/useCedulaLookup";
import { formatPhone } from "../../services/formatPhone";
import { validateCopropietario } from "../../services/validationRules";

const empty = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  nombre_sociedad: "",
  telefono_casa: "",
  telefono_cel: "",
  telefono_trabajo: "",
  placaVehiculo: "",
  placaExtranjera: false,
  notas: "",
};

export default function Modal({
  open,
  onClose,
  formData,
  setFormData,
  mode,
  selectedIndex,
}) {
  const [coOwner, setCoOwner] = useState(empty);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  useEffect(() => {
    if (mode === "edit" && selectedIndex !== null) {
      const selected = formData.copropietarios.find(
        (c) => c.id === selectedIndex,
      );
      setCoOwner(selected ? { ...empty, ...selected } : empty);
    } else {
      setCoOwner(empty);
    }
    setErrors({});
    reset();
  }, [open, mode, selectedIndex]);

  // Aplicar datos del TSE/local
  useEffect(() => {
    if (resultado) {
      setCoOwner((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
      }));
    }
  }, [resultado]);

  if (!open) return null;

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setCoOwner((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleToggle(name) {
    setCoOwner((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  function handleCedulaBlur() {
    buscar(coOwner.cedula);
  }

  function handleSave(e) {
    e.preventDefault(); // evitar que el form del Stepper haga submit
    e.stopPropagation();
    const errs = validateCopropietario(coOwner);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    if (mode === "add") {
      setFormData((prev) => ({
        ...prev,
        copropietarios: [
          ...prev.copropietarios,
          { ...coOwner, id: Date.now() },
        ],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        copropietarios: prev.copropietarios.map((c) =>
          c.id === selectedIndex ? { ...coOwner, id: selectedIndex } : c,
        ),
      }));
    }
    onClose();
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-3">
          {mode === "add" ? "Agregar Copropietario" : "Editar Copropietario"}
        </h2>

        {Object.keys(errors).length > 0 && (
          <div
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderLeft: "3px solid #ef4444",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 12,
              color: "#f87171",
              fontSize: "0.8rem",
            }}
          >
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{ marginRight: 6 }}
            />
            Revisa los campos marcados antes de guardar.
          </div>
        )}

        <div className={styles["form-wrapper"]}>
          {/* Cédula con búsqueda */}
          <div className="row mb-2">
            <div className="form-group col-12">
              <label className="form-label">Cédula</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  name="cedula"
                  type="text"
                  className={`${stepsStyles["form-control"]} form-control`}
                  style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
                  placeholder="Ej: 123456789"
                  value={coOwner.cedula}
                  onChange={handleChange}
                  onBlur={handleCedulaBlur}
                />
                <button
                  type="button"
                  onClick={() => buscar(coOwner.cedula)}
                  disabled={estado === "buscando"}
                  style={{
                    padding: "0 10px",
                    background: "rgba(249,115,22,0.15)",
                    border: "1px solid rgba(249,115,22,0.4)",
                    borderRadius: 6,
                    color: "#f97316",
                    cursor: "pointer",
                    fontSize: "0.78rem",
                  }}
                >
                  {estado === "buscando" ? (
                    <i className="fa-solid fa-circle-notch fa-spin" />
                  ) : (
                    <i className="fa-solid fa-magnifying-glass" />
                  )}
                </button>
              </div>
              <CedulaStatus estado={estado} />
              <FieldError msg={errors.cedula} />
            </div>
          </div>

          {/* Nombre y Apellidos */}
          <div className="form-group row g-3 g-md-4 mb-2">
            <div className="col-12 col-md-6">
              <label className="form-label">Nombre</label>
              <input
                name="nombre"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
                placeholder="Nombre"
                value={coOwner.nombre}
                onChange={handleChange}
              />
              <FieldError msg={errors.nombre} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Apellidos</label>
              <input
                name="apellidos"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: errors.apellidos ? "#ef4444" : undefined,
                }}
                placeholder="Apellidos"
                value={coOwner.apellidos}
                onChange={handleChange}
              />
              <FieldError msg={errors.apellidos} />
            </div>
          </div>

          {/* Nombre Sociedad */}
          <div className="form-group mb-3">
            <label className="form-label">
              Nombre de Sociedad
              <span style={{fontSize:"0.73rem",color:"#94a3b8",marginLeft:8}}>
                (si es persona jurídica)
              </span>
            </label>
            <input
              name="nombre_sociedad"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              placeholder="Ej: Inversiones Sol S.A."
              value={coOwner.nombre_sociedad || ""}
              onChange={handleChange}
            />
          </div>

          {/* Teléfonos */}
          <div className="form-group row g-2 mb-2">
            <div className="col-12 col-md-4">
              <label className="form-label">Tel. Casa</label>
              <input name="telefono_casa" type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder="2222-1234" value={coOwner.telefono_casa || ""} onChange={handleChange}/>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Celular</label>
              <input name="telefono_cel" type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder="8888-1234" value={coOwner.telefono_cel || ""} onChange={handleChange}/>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Tel. Trabajo</label>
              <input name="telefono_trabajo" type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder="2200-1234" value={coOwner.telefono_trabajo || ""} onChange={handleChange}/>
            </div>
          </div>

          {/* Placa y Notas */}
          <div className="form-group row g-2 mb-4">
            <div className="col-12 col-md-6">
              <label className="form-label">Placa del Vehículo</label>
              <input name="placaVehiculo" type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder={coOwner.placaExtranjera ? "Placa extranjera" : "ABC-123"}
                value={coOwner.placaVehiculo} onChange={handleChange}/>
              <ForeignToggle checked={coOwner.placaExtranjera}
                onChange={() => handleToggle("placaExtranjera")} label="Placa extranjera"/>
              <FieldError msg={errors.placaVehiculo}/>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Notas</label>
              <textarea name="notas" rows={2}
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder="Observaciones opcionales..."
                value={coOwner.notas || ""} onChange={handleChange}
                style={{resize:"none"}}/>
            </div>
          </div>

        </div>

        <div className="d-flex gap-2">
          <button type="button" onClick={handleSave} className={styles.btn}>
            Guardar
          </button>
          <button type="button" onClick={onClose} className={styles.btn}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
